<template>
  <div :class="{ fullscreen: fullscreen }" class="tinymce-container" :style="{ width: containerWidth }">
    <textarea :id="tinymceId" class="tinymce-textarea" />
  </div>
</template>

<script>
import plugins from './plugins'
import toolbar from './toolbar'
import load from './dynamicLoadScript'
import api from '@/api/'
import store from '@/store'
import { initEditor, uuid } from './utils'
const QiNiuUrl = store.state.label.data?.QI_NIU_DOMAINS
const tinymceCDN = 'https://style-static.yuzhua-test.com/libraries/tinymce/5.4.2/tinymce.min.js'

export default {
  name: 'Tinymce',
  props: {
    id: {
      type: String,
      default: function () {
        return uuid('vue-tinymce')
      }
    },
    value: {
      type: String,
      default: ''
    },
    initialValue: {
      type: String,
      default: ''
    },
    modelEvents: {
      type: [Array, String],
      default: null
    },
    toolbar: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    menubar: {
      type: String,
      default: '请输入'
    },
    height: {
      type: [Number, String],
      required: false,
      default: 360
    },
    width: {
      type: [Number, String],
      required: false,
      default: 'auto'
    }
  },
  data() {
    return {
      hasChange: false,
      hasInit: false,
      tinymceId: this.id,
      fullscreen: false,
      languageTypeList: {
        'en': 'en',
        'zh': 'zh_CN',
      }
    }
  },
  computed: {
    containerWidth() {
      const width = this.width
      if (/^[\d]+(\.[\d]+)?$/.test(width)) { // matches `100`, `'100'`
        return `${width}px`
      }
      return width
    }
  },
  mounted() {
    this.init()
  },
  activated() {
    if (window.tinymce) {
      this.initTinymce()
    }
  },
  beforeDestroy() {
    this.destroyTinymce()
  },
  deactivated() {
    this.destroyTinymce()
  },
  destroyed() {
    this.destroyTinymce()
  },
  methods: {
    init() {
      load(tinymceCDN, (err) => {
        if (err) {
          this.$message.error(err.message)
          return
        }
        this.initTinymce()
      })
    },
    initTinymce() {
      const _this = this
      window.tinymce.init({
        selector: `#${this.tinymceId}`,
        language: this.languageTypeList['zh'],
        content_style: "p {margin: 0; border:0; padding: 0;}",
        language_url: `${process.env.BASE_URL}tinymce/langs/zh_CN.js`,
        height: this.height,
        body_class: 'panel-body ',
        branding: false, // 隐藏右下角由TINY驱动
        contextmenu_never_use_native: true, // 防止浏览器上下文菜单出现在编辑器中
        elementpath: false, // 隐藏底栏的元素路径（隐藏右下角元素显示）
        object_resizing: true, // 是否允许调整图像大小
        paste_retain_style_properties: "all", // 从Word中复制，保留所有样式
        paste_data_images: true, // 粘贴的同时能把内容里的图片自动上传
        paste_convert_word_fake_lists: false, // 设为false可禁用复制word中的列表内容时，转换为html的UL或OL格式。
        paste_webkit_styles: "all",
        paste_merge_formats: true, // 启用PowerPaste插件的合并格式功能，例如：<b>abc <b>bold</b> 123</b>成为<b>abc bold 123</b>
        end_container_on_empty_block: true, // 如果在空的内部块元素中按下Enter键，则可以使用此选项拆分当前容器块元素。
        powerpaste_word_import: "clean", // 保留标题，表格和列表等内容的结构，但删除内联样式和类。这样就产生了使用站点CSS样式表的简单内容，同时保留了原始文档的语义结构。

        toolbar: this.toolbar.length > 0 ? this.toolbar : toolbar,
        // menubar: this.menubar,
        menubar: false, // 顶部菜单
        plugins: plugins,
        code_dialog_height: 450,
        code_dialog_width: 1000,
        advlist_bullet_styles: "default,circle,disc,square",
        advlist_number_styles:
          "default,lower-alpha,lower-greek,lower-roman,upper-alpha,upper-roman",
        default_link_target: '_blank',
        link_title: false,
        nonbreaking_force_tab: true, // 按tab键插入三个&nbsp;
        init_instance_callback: editor => {
          if (_this.value) {
            editor.setContent(_this.value)
          }
          _this.hasInit = true
        },
        setup(editor) {
          _this.editor = editor
          editor.on('init', (e) => initEditor(e, _this, editor))
          editor.on('FullscreenStateChanged', (e) => {
            _this.fullscreen = e.state
          })
        },
        convert_urls: false,
        images_upload_handler: async (blobInfo, success, failure) => {
          const formData = new FormData()
          const blob = blobInfo.blob()
          formData.append('file', blob)
          const res = await api.QiniuFile(formData)
          if (this.$validate(res)) {
            success(`${QiNiuUrl}/${res.data.url}`)
          } else {
            failure(res.message)
          }
        }
      })
    },
    destroyTinymce() {
      const tinymce = window.tinymce.get(this.tinymceId)
      if (this.fullscreen) {
        tinymce.execCommand('mceFullScreen')
      }

      if (tinymce) {
        tinymce.destroy()
      }
    },
    setContent(value) {
      window.tinymce.get(this.tinymceId).setContent(value)
    },
    getContent() {
      window.tinymce.get(this.tinymceId).getContent()
    },
    imageSuccessCBK(arr) {
      arr.forEach(v => window.tinymce.get(this.tinymceId).insertContent(`<img class="wscnph" src="${v.url}" >`))
    },
    // async uploadOss(file) {
    //   const file_name = file.name
    //   this.client = await OSSClient()
    //   const file_path = `message-center/${moment().format(
    //     "YYYY/MM/"
    //   )}${file_name}`
    //   const fileUrl = await this.putObject(file.file, file_path)
    //   return fileUrl
    // },
    // base64转blob
    dataURLtoFile(dataurl, filename) {
      var mime = "png"
      var bstr = atob(dataurl)
      var n = bstr.length
      var u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    async putObject(data, file_path) {
      try {
        const result = await this.client.put(file_path, data)
        if (result.res.status === 200) {
          return result.res.requestUrls[0]
        } else {
          this.$message.error("上传文件至阿里云失败！")
        }
      } catch (e) {
        console.log("err", e)
      }
    },
  }
}
</script>

<style lang="less" scoped>
.tinymce-container {
  position: relative;
  line-height: normal;
}

.tinymce-container {
  ::v-deep {
    .mce-fullscreen {
      z-index: 10000;
    }
  }
}

.tinymce-textarea {
  visibility: hidden;
  z-index: -1;
}

.editor-custom-btn-container {
  position: absolute;
  right: 4px;
  top: 4px;
  /*z-index: 2005;*/
}

.fullscreen .editor-custom-btn-container {
  z-index: 10000;
  position: fixed;
}

.editor-upload-btn {
  display: inline-block;
}
</style>
